import { ThreadEcho } from "@/components/ThreadEcho";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSpark } from "@/lib/store";
import { getStrengthsPrompts, saveStrengthsEvidence, noticeStrengths, respondStrength, patchProfile, getProfile } from "@/lib/api";
import { ModeTag } from "@/components/ActivityMode";
import GoldThreadPanel from "@/components/GoldThread";
import { TalkInput } from "@/components/TalkInput";
import { EVIDENCE_MAPS, HELD_FIELD_LABELS } from "@/lib/evidenceMaps";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

const Movement = ({ mode, eyebrow, title, children, testid }) => {
  const bg = mode === "reflect" ? "hsl(var(--spark-warmth))" : mode === "notice" ? "hsl(var(--spark-cool))" : "hsl(var(--spark-ivory))";
  return (
    <section data-testid={testid} className="grid md:grid-cols-12 gap-6 md:gap-10">
      <div className="md:col-span-3">
        <div className="md:sticky md:top-28 rounded-2xl p-5" style={{ background: bg }}>
          <ModeTag mode={mode} />
          <p className="mt-4 spark-eyebrow">{eyebrow}</p>
          <p className="mt-2 spark-voice text-xl leading-snug text-[#17171C]">{title}</p>
        </div>
      </div>
      <div className="md:col-span-9">{children}</div>
    </section>
  );
};

const Interpretation = ({ item, onRespond, busy, cfg }) => {
  const isTension = item.kind === "tension";
  const [mode, setMode] = useState(null);
  const [correction, setCorrection] = useState("");
  const pending = item.status === "pending";
  return (
    <article data-testid={`strength-interpretation-${item.id}`} data-status={item.status}
             className={`spark-voice-block ${pending ? "" : "opacity-80"}`}>
      {item.revision_of && (
        <p className="spark-eyebrow-copper mb-3">Revised with your correction</p>
      )}
      <p className="spark-eyebrow mb-2">{isTension ? "Worth noticing" : "What I'm noticing"}</p>
      <p data-testid="strength-notice" className="spark-voice text-2xl leading-snug text-[#17171C]">{item.notice}</p>

      <p className="spark-eyebrow mt-7 mb-3">You described</p>
      <ul data-testid="strength-evidence-list" className="space-y-2.5">
        {item.evidence_items.map((e, i) => (
          <li key={i} className="flex items-start gap-3">
            <span className="mt-2.5 h-1 w-1 rounded-full bg-[#2C50A0] shrink-0" />
            <div>
              <p className="text-[#17171C] leading-relaxed">{e.quote}</p>
              <p className="text-xs text-[#8A8A91] mt-0.5">from · {e.source_label}</p>
            </div>
          </li>
        ))}
      </ul>

      <p className="spark-eyebrow-copper mt-7 mb-2">{isTension ? "Left open, for you" : "One possibility"}</p>
      <p data-testid="strength-interpretation-text" className="spark-voice text-xl sm:text-2xl leading-snug" style={{ color: "#1F3D80" }}>{item.interpretation}</p>
      <ThreadEcho theme={item.thread_echo} className="mt-3" />
      <p className="mt-2 text-sm text-[#56565D]" data-testid="strength-kind" data-kind={item.kind}>{cfg.kindLabels[item.kind] || cfg.kindLabels[Object.keys(cfg.kindLabels)[0]]} · <span className="text-[#17171C]" data-testid="strength-label">{item.label || item.strength_label}</span></p>
      {item.tension && (
        <p data-testid="strength-tension" className="mt-4 text-sm text-[#56565D] leading-relaxed border-l border-[rgba(0,0,0,0.12)] pl-4">
          <span className="spark-eyebrow block mb-1">A tension worth holding</span>{item.tension}
        </p>
      )}

      {pending && !mode && (
        <>
          <p className="mt-7 text-sm text-[#8A8A91]">{isTension ? "Does this tension feel real to you?" : "Does that feel accurate?"}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button data-testid="strength-yes-btn" disabled={busy} onClick={() => onRespond(item.id, "yes", "")} className="spark-btn-copper disabled:opacity-60">Yes</button>
            <button data-testid="strength-partly-btn" disabled={busy} onClick={() => setMode("partly")} className="spark-btn-secondary">Partly</button>
            <button data-testid="strength-no-btn" disabled={busy} onClick={() => setMode("no")} className="spark-btn-secondary">No</button>
          </div>
        </>
      )}

      {pending && mode && (
        <div className="mt-5 max-w-2xl">
          <p className="spark-voice text-lg text-[#17171C] mb-3">
            {mode === "partly" ? "Which part fits, and which part doesn't?" : "Tell me what I'm missing."}
          </p>
          <TalkInput testid="strength-correction-input" rows={3} value={correction} onChange={setCorrection} placeholder="In your own words." />
          <div className="mt-3 flex gap-2">
            <button data-testid="strength-submit-correction-btn" disabled={busy || !correction.trim()} onClick={() => onRespond(item.id, mode, correction)} className="spark-btn-primary disabled:opacity-60">
              {busy ? "Recalibrating…" : mode === "partly" ? "Revise it" : "Set it aside"}
            </button>
            <button onClick={() => { setMode(null); setCorrection(""); }} className="spark-btn-secondary">Cancel</button>
          </div>
        </div>
      )}

      {!pending && (
        <div data-testid="strength-status" className="mt-6 flex items-center gap-2 text-sm text-[#8A8A91]">
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: item.status === "confirmed" ? "#2C50A0" : "#8A8A91" }} />
          {item.status === "confirmed" && (isTension ? "Held as a tension — not resolved. It's yours to make meaning from." : "Held in your Career Compass, with its evidence.")}
          {item.status === "partly" && "Partly — Spark revised this with your correction."}
          {item.status === "rejected" && `Set aside. Spark won't hold this as a ${isTension ? "tension" : cfg.noun}.`}
        </div>
      )}
    </article>
  );
};

export default function EvidenceMap({ activityKey = "strengths-evidence-map" }) {
  const cfg = EVIDENCE_MAPS[activityKey];
  const { userId, profile, setProfile } = useSpark();
  const nav = useNavigate();
  const [prompts, setPrompts] = useState([]);
  const [evidence, setEvidence] = useState({});
  const [saveState, setSaveState] = useState("idle");
  const [busy, setBusy] = useState(false);
  const saveTimer = useRef(null);
  const loadedFor = useRef(null);
  const noticeRef = useRef(null);

  useEffect(() => { getStrengthsPrompts(userId, cfg.prefix).then(d => setPrompts(d.prompts)); }, [userId, cfg.prefix]);
  useEffect(() => {
    if (!profile || loadedFor.current === `${userId}:${activityKey}`) return;
    loadedFor.current = `${userId}:${activityKey}`;
    setEvidence(profile[cfg.evidenceField] || {});
  }, [profile, userId, activityKey, cfg.evidenceField]);

  const smap = profile?.[cfg.mapField] || {};
  const interps = smap.interpretations || [];
  const noticing = !!smap.noticing;
  const revising = smap.revising || null;
  const pendingList = interps.filter(i => i.status === "pending");

  // Background jobs (LLM) — poll the profile until the flag clears.
  useEffect(() => {
    if (!noticing && !revising) return undefined;
    const started = Date.now();
    const t = setInterval(async () => {
      let p;
      try { p = await getProfile(userId); } catch { return; }
      setProfile(p);
      const sm = p[cfg.mapField] || {};
      if (!sm.noticing && !sm.revising) {
        clearInterval(t);
        if (sm.error) toast(sm.error);
        else if (noticing) setTimeout(() => noticeRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
      } else if (Date.now() - started > 180000 || (sm.noticing?.started_at && Date.now() - new Date(sm.noticing.started_at).getTime() > 240000)) {
        clearInterval(t);
        setProfile({ ...p, [cfg.mapField]: { ...sm, noticing: null, revising: null } });
        toast("Spark lost the thread while reading. Please try Look again.");
      }
    }, 2500);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noticing, revising, userId]);
  const answered = interps.filter(i => i.status !== "pending" && i.status !== "confirmed");
  const filled = useMemo(() => Object.values(evidence).filter(v => v && v.trim()).length, [evidence]);
  const heldItems = [...cfg.heldFields, "tensions"].flatMap(f => (profile?.[f] || []).filter(s => s?.source_activity === activityKey).map(s => ({ ...s, field: f })));

  const onChange = (key, value) => {
    const next = { ...evidence, [key]: value };
    setEvidence(next);
    clearTimeout(saveTimer.current);
    setSaveState("saving");
    saveTimer.current = setTimeout(async () => {
      try {
        setProfile(await saveStrengthsEvidence(userId, next, cfg.prefix));
        setSaveState("saved");
      } catch {
        setSaveState("unsaved");
        saveTimer.current = setTimeout(() => onChange(key, next[key]), 4000);
      }
    }, 900);
  };

  const notice = async () => {
    try {
      setProfile(await noticeStrengths(userId, evidence, cfg.prefix));
    } catch (e) {
      toast(e?.response?.data?.detail || "Something interrupted us. Please try again.");
    }
  };

  const respond = async (id, status, correction) => {
    setBusy(true);
    try {
      const p = await respondStrength(userId, id, { status, correction }, cfg.prefix);
      let next = p;
      if (status === "yes" && !p.completed_activities?.includes(activityKey)) {
        next = await patchProfile(userId, "completed_activities", [...(p.completed_activities || []), activityKey]);
      }
      setProfile(next);
      if (status === "yes") toast("Held in your Career Compass, with its evidence.");
      else if (status === "partly") toast("Thank you. Spark is revising with your correction.");
      else toast("Set aside. Spark won't hold this.");
    } catch (e) {
      toast(e?.response?.data?.detail || "Something interrupted us. Your answer wasn't lost — please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (!profile) return <div className="text-[#8A8A91]">Loading…</div>;

  return (
    <div className="max-w-5xl mx-auto space-y-16">
      <div>
        <button data-testid="activity-back-btn" onClick={() => nav("/part-one")} className="text-sm text-[#8A8A91] hover:text-[#17171C] inline-flex items-center gap-1.5 mb-8">
          <ArrowLeft size={14} /> Part One
        </button>
        <header className="grid md:grid-cols-12 gap-8">
          <div className="md:col-span-8">
            <p className="spark-eyebrow-copper spark-eyebrow-with-dot">{cfg.stage}</p>
            <h1 data-testid="activity-title" className="mt-3 text-4xl sm:text-5xl leading-tight text-[#17171C] font-medium tracking-tight">{cfg.title}</h1>
            <p className="mt-6 spark-voice text-2xl sm:text-3xl leading-snug text-[#17171C] max-w-2xl">{cfg.opening}</p>
          </div>
          <aside className="md:col-span-4 md:pt-10 text-sm text-[#56565D] space-y-4">
            <p className="leading-relaxed">{cfg.aside}</p>
            <p className="text-xs text-[#8A8A91]">About {cfg.minutes} minutes · saves as you go</p>
          </aside>
        </header>
      </div>

      {/* REFLECT — you supply the examples */}
      <Movement mode="reflect" eyebrow="Your evidence" title={cfg.reflectTitle} testid="sem-reflect">
        <ol className="divide-y divide-[rgba(0,0,0,0.08)] border-t border-[rgba(0,0,0,0.08)]">
          {prompts.map((p, i) => (
            <li key={p.key} data-testid={`evidence-prompt-${p.key}`} className="py-6 grid sm:grid-cols-12 gap-3 sm:gap-6">
              <div className="sm:col-span-4">
                <p className="spark-eyebrow">{String(i + 1).padStart(2, "0")} · {p.label}</p>
              </div>
              <div className="sm:col-span-8">
                <p className="spark-voice text-lg leading-snug text-[#17171C]">{p.question}</p>
                <TalkInput
                  testid={`evidence-input-${p.key}`}
                  rows={3}
                  value={evidence[p.key] || ""}
                  onChange={v => onChange(p.key, v)}
                  placeholder="An example, not a label."
                  className="mt-3"
                />
              </div>
            </li>
          ))}
        </ol>
        <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
          <span data-testid="evidence-count" className="text-sm text-[#8A8A91]">
            {filled} of {prompts.length || 8} examples{saveState === "saving" ? " · saving…" : saveState === "saved" ? " · saved" : saveState === "unsaved" ? " · couldn't save just now, retrying" : ""}
            {filled < 3 && " — Spark needs at least three before it looks for patterns"}
          </span>
          <button data-testid="sem-notice-btn" disabled={noticing || filled < 3} onClick={notice} className="spark-btn-primary disabled:opacity-60">
            {noticing ? "Reading across your examples…" : interps.length ? "Look again" : "See what Spark notices"}
          </button>
        </div>
      </Movement>

      {/* NOTICE — Spark connects the evidence */}
      <div ref={noticeRef}>
        <Movement mode="notice" eyebrow="Spark connects the evidence" title={interps.length ? cfg.noticeTitle : "Nothing yet. Spark only speaks once it has read your examples."} testid="sem-notice">
          {noticing && (
            <div data-testid="sem-noticing" className="spark-voice-block">
              <p className="spark-voice text-2xl text-[#17171C] leading-snug">Reading across your examples, looking for what repeats…</p>
              <p className="mt-2 text-sm text-[#8A8A91]">This takes a moment. Spark is checking each pattern against at least two of your examples.</p>
            </div>
          )}
          {!noticing && pendingList.length === 0 && interps.length === 0 && (
            <p className="text-[#56565D] max-w-xl">When you're ready, Spark will read across everything above and offer a few possible {cfg.nounPlural} — with the evidence beside each, any tensions left open, and a question: does that feel accurate?</p>
          )}
          {!noticing && smap.recalibration && (
            <div data-testid="sem-recalibration" className="mb-12 rounded-2xl p-6" style={{ background: "hsl(var(--spark-canvas))", border: "1px solid rgba(44,80,160,0.35)" }}>
              <p className="spark-eyebrow-copper mb-2">Taking your correction on board</p>
              <p className="spark-voice text-xl leading-snug text-[#17171C]">{smap.recalibration}</p>
              {smap.dropped_repeats?.length > 0 && (
                <p className="mt-3 text-xs text-[#8A8A91]">Spark also held back {smap.dropped_repeats.length} reading{smap.dropped_repeats.length > 1 ? "s" : ""} that leaned on the same evidence you'd set aside.</p>
              )}
            </div>
          )}
          {revising && (
            <div data-testid="sem-revising" className="mb-10 spark-voice-block">
              <p className="spark-voice text-xl text-[#17171C] leading-snug">Revising with your correction…</p>
            </div>
          )}
          <div className="space-y-14">
            {pendingList.map(item => <Interpretation key={item.id} item={item} onRespond={respond} busy={busy} cfg={cfg} />)}
          </div>
          {!noticing && !revising && interps.length > 0 && pendingList.length === 0 && (
            <p data-testid="sem-all-answered" className="text-[#56565D] max-w-xl">
              You've answered everything Spark offered. Add more evidence above and choose <span className="text-[#17171C]">Look again</span> if you'd like it to look for other patterns — it will honour what you've set aside.
            </p>
          )}
          {answered.length > 0 && (
            <details className="mt-12 group">
              <summary data-testid="sem-answered-toggle" className="cursor-pointer text-sm text-[#8A8A91] hover:text-[#17171C]">What you corrected or set aside ({answered.length})</summary>
              <div className="mt-8 space-y-12">
                {answered.map(item => <Interpretation key={item.id} item={item} onRespond={respond} busy={busy} cfg={cfg} />)}
              </div>
            </details>
          )}
        </Movement>
      </div>

      {/* ACT — what you've confirmed, and what it connects to */}
      <Movement mode="act" eyebrow="Held in your Compass" title={heldItems.length ? `${cfg.noun[0].toUpperCase() + cfg.noun.slice(1)} → supporting examples → where this came from.` : "Nothing is held until you say it's true."} testid="sem-act">
        {heldItems.length === 0 ? (
          <p className="text-[#56565D] max-w-xl">What you confirm appears here with its evidence, and in your Career Compass. Tensions are held as tensions — not resolved. If something connects to what Spark has noticed elsewhere, it may offer a Gold Thread, which you'll also be asked to check.</p>
        ) : (
          <ul data-testid="sem-confirmed-list" className="space-y-8">
            {heldItems.map((s, i) => (
              <li key={i} data-testid="sem-confirmed-strength" className="grid sm:grid-cols-12 gap-3 sm:gap-6">
                <div className="sm:col-span-4">
                  <p className="text-xl text-[#17171C] font-medium tracking-tight">{s.label}</p>
                  <p className="mt-1 text-xs text-[#8A8A91]">{cfg.kindLabels[s.kind] || HELD_FIELD_LABELS[s.field]} · from {cfg.title}</p>
                </div>
                <div className="sm:col-span-8">
                  <p className="spark-voice text-[#56565D] leading-snug">{s.evidence}</p>
                  <ul className="mt-3 space-y-1.5">
                    {(s.evidence_items || []).map((e, j) => (
                      <li key={j} className="text-sm text-[#17171C] flex items-start gap-3">
                        <span className="mt-2 h-px w-4 bg-[#2C50A0] shrink-0" />
                        <span>{e.quote} <span className="text-[#8A8A91]">· {e.source_label}</span></span>
                      </li>
                    ))}
                  </ul>
                </div>
              </li>
            ))}
          </ul>
        )}
        {(profile.gold_threads_pending || []).some(t => t.origin === activityKey) && (
          <div className="mt-14" data-testid="sem-gold-thread">
            <GoldThreadPanel />
          </div>
        )}
        <div className="mt-12 flex flex-wrap gap-3">
          <Link to="/compass" data-testid="sem-open-compass-btn" className="spark-btn-secondary">Open my Compass</Link>
          <Link to={`/part-one/${cfg.next.key}`} data-testid="activity-next-btn" className="spark-btn-primary">Next · {cfg.next.title}</Link>
        </div>
      </Movement>
    </div>
  );
}
